import { getPolishNumberConjugation } from "@jutro/tools";
import { Avatar, Button, MessageBox, Table, Tag } from "@jutro/ui";
import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { envVars } from "envvars";
import { useState } from "react";
import { useScheduleVisitMutations } from "components/ScheduleVisitForm/hooks/useScheduleVisitMutations";
import { languageToEmojiMap } from "components/ScheduleVisitForm/tools";
import {
  ScheduleVisitTableData,
  ScheduleVisitTileData,
} from "components/ScheduleVisitForm/types";
import { Loader } from "components/new";
import {
  DoctorGetVisitDataForEditFormQuery,
  DoctorMSPShiftVisitType,
  DoctorMSSupportedLanguage,
} from "lib/graphql/megaSchema";
import { useTileSystem } from "lib/tools/createWorkspace/useTileSystem";
import { avatarConfig } from "lib/tools/httpConfigs";

type Props = {
  visitType: DoctorMSPShiftVisitType;
  shifts: ScheduleVisitTableData[] | null;
  scheduleVisitTileData: ScheduleVisitTileData;
  visitData: DoctorGetVisitDataForEditFormQuery | undefined;
  isLoading: boolean;
};

export const ScheduleVisitTable = ({
  visitType,
  shifts,
  scheduleVisitTileData,
  isLoading,
  visitData,
}: Props) => {
  const { id, mode, patientId } = scheduleVisitTileData;

  const { removeTile } = useTileSystem();

  const { createVisit, editVisit, loading } = useScheduleVisitMutations({
    scheduleVisitTileData,
    visitData,
  });

  const [selectedRow, setSelectedRow] = useState<ScheduleVisitTableData | null>(
    null,
  );

  const scheduleVisitTableColumns: ColumnDef<ScheduleVisitTableData>[] = [
    {
      accessorKey: "shiftDate",
      header: "",
      cell: ({ getValue, table }) => {
        const currentShiftDate = getValue<string>();

        const matchingRowsCount = table.options.data.filter(
          (row) => row.shiftDate === currentShiftDate,
        ).length;

        return (
          <div className="absolute top-1/2 left-[60px] flex -translate-y-1/2 items-center gap-2.5">
            <span className="font-label">
              {dayjs(currentShiftDate).format("D MMMM, dddd")}
            </span>

            <Tag
              text={`${matchingRowsCount} ${getPolishNumberConjugation(matchingRowsCount, ["wizyta", "wizyty", "wizyt"])}`}
              color="medium-gray"
            />
          </div>
        );
      },
    },
    {
      accessorKey: "scheduledOn",
      header: () => <div className="w-12">Godzina</div>,
      cell: ({ getValue }) => (
        <span className="font-label w-12">
          {dayjs(getValue<string>()).format("HH:mm")}
        </span>
      ),
    },
    {
      id: "staff",
      accessorKey: "staff",
      header: () => <div className="w-58">Personel</div>,
      cell: ({ getValue }) => {
        const { id, firstname, lastname } =
          getValue<ScheduleVisitTableData["staff"]>();

        return (
          <div className="flex w-58 gap-3">
            <div className="h-6 w-6">
              <Avatar
                type="photo"
                imgSrc={`${avatarConfig[envVars.REACT_APP_CONFIG]}/${id}`}
              />
            </div>
            <span className="font-label">
              {firstname} {lastname}
            </span>
          </div>
        );
      },
    },
    {
      id: "languages",
      header: () => "Języki",
      cell: ({ row }) => {
        const languages = row.original.languages.map((lang) =>
          lang.toUpperCase(),
        ) as DoctorMSSupportedLanguage[];

        return (
          <div className="flex h-6 items-center gap-2.5">
            {languages.map((lang) => languageToEmojiMap[lang])}
          </div>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <div className="bg-jutro-new-warm-gray-50 w-full flex-1 animate-pulse rounded-2xl duration-300" />
    );
  }

  if (!shifts) {
    return null;
  }

  if (shifts.length === 0) {
    return <MessageBox color="rose" text="Brak wolnych terminów" />;
  }

  const handleClick = async (selectedRow: ScheduleVisitTableData) => {
    const shift = selectedRow.shift;

    if (mode === "edit" && visitData?.doctorVisit) {
      await editVisit({
        id: visitData.doctorVisit.id,
        visitType,
        shiftsSlot: [shift],
      });
    } else {
      await createVisit({
        shiftVisitType: visitType,
        shiftsSlot: [shift],
        patientId,
      });
    }

    removeTile(id);
  };

  return (
    <>
      <div className="min-h-0 w-full">
        <Table
          columns={scheduleVisitTableColumns}
          initiallyExpandOnlyFirstRow
          data={shifts}
          groupBy="shiftDate"
          disabled={loading}
          selectionRowMode="single"
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
        />
      </div>

      {selectedRow && (
        <div className="flex w-full grow flex-col justify-end">
          <Button
            text="Umów wizytę"
            type="button"
            loading={loading}
            onClick={() => handleClick(selectedRow)}
          />
        </div>
      )}
    </>
  );
};
